/**
 * Brand Reset Password Component
 * Handles the password reset confirmation after clicking the email link.
 * Supabase redirects here with access_token in the URL hash fragment.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../assets/css/brand-portal.css';

// Use environment variable or default based on environment
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (process.env.NODE_ENV === 'production') {
    return 'https://yemberzal-app-28481d1d0b39.herokuapp.com';
  }
  return 'http://localhost:8000';
};

const API_URL = getApiUrl();

function BrandResetPassword() {
  const navigate = useNavigate();

  const [accessToken, setAccessToken] = useState('');
  const [tokenError, setTokenError] = useState('');
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Extract access_token or error info from URL hash on mount
  useEffect(() => {
    const hash = window.location.hash.substring(1); // remove leading #
    const params = new URLSearchParams(hash);

    // Check for Supabase error params first
    const errorParam = params.get('error');
    const errorCode = params.get('error_code');
    const errorDescription = params.get('error_description');

    if (errorParam || errorCode) {
      // Supabase redirected with an error (e.g. otp_expired, access_denied)
      const friendlyMessages = {
        otp_expired: 'This password reset link has expired. Please request a new one.',
        access_denied: 'This password reset link is no longer valid. Please request a new one.',
      };
      const message =
        friendlyMessages[errorCode] ||
        (errorDescription
          ? decodeURIComponent(errorDescription.replace(/\+/g, ' '))
          : 'This reset link is invalid or has expired. Please request a new one.');
      setTokenError(message);
      return;
    }

    const token = params.get('access_token');
    const type = params.get('type');

    if (token && type === 'recovery') {
      setAccessToken(token);
    } else if (token) {
      // Some Supabase versions may not include type
      setAccessToken(token);
    } else {
      setTokenError(
        'Invalid or missing reset link. Please request a new password reset.'
      );
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    setError('');
  };

  const validateForm = () => {
    const errors = {};

    if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/[A-Z]/.test(formData.password)) {
      errors.password = 'Password must contain at least one uppercase letter';
    } else if (!/[a-z]/.test(formData.password)) {
      errors.password = 'Password must contain at least one lowercase letter';
    } else if (!/[0-9]/.test(formData.password)) {
      errors.password = 'Password must contain at least one number';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/brand/reset-password/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: accessToken,
          new_password: formData.password,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/brand-portal/login');
        }, 3000);
      } else {
        setError(data.error || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Token error state
  if (tokenError) {
    return (
      <div className="brand-auth-page">
        <div className="brand-auth-container">
          <div className="brand-auth-card">
            <div className="brand-auth-header">
              <Link to="/">
                <img
                  src={require('../assets/images/Y.png')}
                  alt="Yemberzal"
                  className="brand-auth-logo"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/default-image.avif';
                  }}
                />
              </Link>
              <h1 className="brand-auth-title">Invalid Reset Link</h1>
            </div>

            <div className="brand-alert brand-alert-error">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {tokenError}
            </div>

            <Link
              to="/brand-portal/forgot-password"
              className="brand-btn brand-btn-primary"
              style={{ textDecoration: 'none', marginTop: 16 }}
            >
              Request New Reset Link
            </Link>

            <div className="brand-auth-footer">
              <Link to="/brand-portal/login" style={{ color: 'inherit', textDecoration: 'none' }}>
                &larr; Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="brand-auth-page">
        <div className="brand-auth-container">
          <div className="brand-auth-card">
            <div className="brand-auth-header">
              <div style={{
                width: 64,
                height: 64,
                background: 'rgba(34, 197, 94, 0.1)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h1 className="brand-auth-title">Password Reset!</h1>
              <p className="brand-auth-subtitle">
                Your password has been updated successfully. Redirecting you to login...
              </p>
            </div>

            <Link to="/brand-portal/login" className="brand-btn brand-btn-primary" style={{ textDecoration: 'none' }}>
              Continue to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="brand-auth-page">
      <div className="brand-auth-container">
        <div className="brand-auth-card">
          <div className="brand-auth-header">
            <Link to="/">
              <img
                src={require('../assets/images/Y.png')}
                alt="Yemberzal"
                className="brand-auth-logo"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = '/default-image.avif';
                }}
              />
            </Link>
            <h1 className="brand-auth-title">Set New Password</h1>
            <p className="brand-auth-subtitle">
              Enter your new password below
            </p>
          </div>

          {error && (
            <div className="brand-alert brand-alert-error">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="brand-form-group">
              <label htmlFor="password" className="brand-form-label">New Password</label>
              <input
                type="password"
                id="password"
                name="password"
                className={`brand-form-input ${validationErrors.password ? 'error' : ''}`}
                placeholder="Create a strong password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                autoFocus
              />
              {validationErrors.password && (
                <p className="brand-form-error">{validationErrors.password}</p>
              )}
              <p className="brand-form-hint">
                Must be at least 8 characters with uppercase, lowercase, and a number
              </p>
            </div>

            <div className="brand-form-group">
              <label htmlFor="confirmPassword" className="brand-form-label">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className={`brand-form-input ${validationErrors.confirmPassword ? 'error' : ''}`}
                placeholder="Confirm your new password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
              {validationErrors.confirmPassword && (
                <p className="brand-form-error">{validationErrors.confirmPassword}</p>
              )}
            </div>

            <button
              type="submit"
              className="brand-btn brand-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="brand-spinner" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>

          <div className="brand-auth-footer">
            <Link to="/brand-portal/login" style={{ color: 'inherit', textDecoration: 'none' }}>
              &larr; Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BrandResetPassword;
